import React, { useState, useEffect } from 'react';
import { Bell } from 'lucide-react';
import { ConnectionRequestCard } from './ConnectionComponents';
import { toast } from 'react-hot-toast';

const ConnectionRequests = ({ onRequestHandled }) => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processedRequests, setProcessedRequests] = useState(new Set());

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BASE_URL}/api/connections/pending`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (!response.ok) throw new Error('Failed to fetch requests');
      const data = await response.json();
      setRequests(data.filter(req => !processedRequests.has(req._id)));
    } catch (err) {
      console.error('Error fetching requests:', err);
      toast.error('Failed to load connection requests');
    } finally {
      setLoading(false);
    }
  };

  const handleRequestResponse = async (requestId, status) => {
    try {
      setProcessedRequests(prev => new Set(prev).add(requestId));
      
      // Optimistically update UI
      setRequests(prev => prev.filter(req => req._id !== requestId));

      const response = await fetch(`${process.env.REACT_APP_BASE_URL}/api/connections/${requestId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ status })
      });

      if (!response.ok) {
        throw new Error('Failed to update request');
      }
      
      const updatedConnection = await response.json();
      
      // If request was accepted, trigger parent component update
      if (status === 'accepted' && onRequestHandled) {
        onRequestHandled(updatedConnection);
        toast.success('Connection request accepted');
      } else if (status === 'rejected') {
        toast.success('Connection request rejected');
      }

    } catch (err) {
      console.error('Error updating request:', err);
      toast.error('Failed to process connection request');
      
      // Revert optimistic update on error
      setProcessedRequests(prev => {
        const newSet = new Set(prev);
        newSet.delete(requestId);
        return newSet;
      });
      fetchRequests(); // Reload the requests
    }
  };

  if (loading) {
    return (
      <div className="p-4 flex justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
      </div>
    );
  }

  if (requests.length === 0) {
    return (
      <div className="p-8 text-center">
        <Bell className="h-12 w-12 text-gray-400 mx-auto" />
        <h3 className="mt-2 text-sm font-medium text-gray-900">No pending requests</h3>
        <p className="mt-1 text-sm text-gray-500">
          When someone sends you a connection request, it will appear here
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4 p-4">
      {requests.map(request => (
        <ConnectionRequestCard
          key={request._id}
          connection={request}
          onAccept={() => handleRequestResponse(request._id, 'accepted')}
          onReject={() => handleRequestResponse(request._id, 'rejected')}
        />
      ))}
    </div>
  );
};

export default ConnectionRequests;